import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { useEffect, useState } from 'react';
import * as pluralize from 'pluralize';
import { Card, Checkbox, FormControlLabel, Grid, GridItem, GridRow, Radio, RadioGroup, Spacer, Typography } from '@emburse/ui-core';
import { AbacusCurrency } from '@Shared/helpers';
import { ShippingOptionServiceEnum } from '@Shared/interfaces/CardsGatewayInterfaces';
let radioButtonRefs = {
    [ShippingOptionServiceEnum.Standard]: null,
    [ShippingOptionServiceEnum.Express]: null,
    [ShippingOptionServiceEnum.Priority]: null
};
export const ShippingMethodFormBase = ({ onChange, shippingMethods, requireSignature, service }) => {
    var _a;
    const [shippingOptionWithRequireSignature, setShippingOptionWithRequireSignature] = useState({});
    useEffect(() => {
        var _a;
        const newShippingOption = shippingMethods && ((_a = shippingMethods[service]) === null || _a === void 0 ? void 0 : _a.find((method) => Boolean(method.requireDeliverySignatureFeeAmount)));
        setShippingOptionWithRequireSignature(newShippingOption);
    }, [shippingMethods, service]);
    radioButtonRefs = {
        [ShippingOptionServiceEnum.Standard]: React.useRef(null),
        [ShippingOptionServiceEnum.Express]: React.useRef(null),
        [ShippingOptionServiceEnum.Priority]: React.useRef(null)
    };
    if (shippingMethods === undefined || requireSignature === undefined || service === undefined) {
        return _jsx(_Fragment, {});
    }
    return (_jsxs(Grid, Object.assign({ container: true, spacing: 2, "data-testid": "Shipping-Method-Form-Container" }, { children: [_jsx(Grid, Object.assign({ item: true, xs: 12 }, { children: _jsx(Typography, Object.assign({ variant: "subtitle1", "data-testid": "Shipping-Method-Form-Title" }, { children: "Shipping Method" })) })), ((_a = shippingMethods[ShippingOptionServiceEnum.Express]) === null || _a === void 0 ? void 0 : _a.length) > 0 && (_jsxs(_Fragment, { children: [_jsx(Grid, Object.assign({ item: true, xs: 12 }, { children: renderShippingSelectorRadioCards(shippingMethods, service || ShippingOptionServiceEnum.Standard, onChange) })), shippingOptionWithRequireSignature && (_jsx(Grid, Object.assign({ item: true, xs: 12 }, { children: renderShippingRequireSignatureCheckbox(['Priority', 'Express'].includes(shippingOptionWithRequireSignature.service), requireSignature, shippingOptionWithRequireSignature.requireDeliverySignatureFeeAmount || 0, onChange) })))] })), _jsx(Spacer, { size: 16 })] })));
};
function handleCardClick(refs, serviceName) {
    refs[serviceName].current.click();
}
function renderShippingSelectorRadioCards(methods, service, onChange) {
    const shippingMethodList = [methods[ShippingOptionServiceEnum.Standard], methods[ShippingOptionServiceEnum.Express]];
    return (_jsx(RadioGroup, Object.assign({ defaultValue: service, "data-testid": "Shipping-Method-Form-Radio-Group", onChange: (e) => {
            const changedValues = {
                shippingService: e.target.value
            };
            if (e.target.value === 'Standard') {
                changedValues.shippingRequireSignature = false; // standard shipping does not support signature
            }
            onChange(changedValues);
        } }, { children: shippingMethodList.map((options, index) => {
            const method = options[0];
            const feeFormatted = new AbacusCurrency(method.baseFeeAmount || 0, method.baseFeeCurrency, true).toFormattedString();
            return (_jsxs("div", { children: [_jsx(Card, Object.assign({ className: "shipping-method-card", onClick: () => {
                            handleCardClick(radioButtonRefs, method.service);
                        } }, { children: _jsxs(GridRow, Object.assign({ spacing: 2, justifyContent: "space-between", alignItems: "flex-start" }, { children: [_jsxs(GridItem, Object.assign({ xs: 10 }, { children: [_jsx(FormControlLabel, { value: method.service, control: _jsx(Radio, {}), label: renderShippingOptionName(method), ref: radioButtonRefs[method.service] }), _jsx(Typography, Object.assign({ color: "textSecondary", variant: "body2", className: "shipping-method-description" }, { children: renderShippingOptionDescription(method) }))] })), _jsx(GridItem, Object.assign({ xs: 2 }, { children: _jsx(Typography, Object.assign({ style: { marginTop: '9px' } }, { children: feeFormatted })) }))] })) })), index + 1 < shippingMethodList.length && _jsx(Spacer, { size: 16 })] }, method.service));
        }) })));
}
function renderShippingOptionDescription(method) {
    // Standard: 7-10 business days
    // Priority: 5-7 business days with real-time tracking and optional signature. Charge is non-refundable.
    if (method.service === 'Standard') {
        return method.estArrivalMinBusinessDays + ' - ' + method.estArrivalMaxBusinessDays + ' business days';
    }
    else if (method.service === 'Express') {
        let dateRangeText = '';
        if (method.estArrivalMinBusinessDays === method.estArrivalMaxBusinessDays) {
            // e.g. 2 business days
            dateRangeText = method.estArrivalMinBusinessDays + ' business ' + pluralize('day', method.estArrivalMinBusinessDays);
        }
        else {
            // e.g. 2 - 4 business days
            dateRangeText = method.estArrivalMinBusinessDays + ' - ' + method.estArrivalMaxBusinessDays + ' business days';
        }
        return dateRangeText + ' with real-time tracking and optional signature. Charge is non-refundable.';
    }
    return '';
}
function renderShippingOptionName(method) {
    if (method.service === 'Standard') {
        return 'Standard';
    }
    else if (method.service === 'Express') {
        return 'Expedited';
    }
    else if (method.service === 'Priority') {
        return 'Priority';
    }
    return null;
}
function renderShippingRequireSignatureCheckbox(available = false, checked = false, fee = 0, onChange) {
    const feeFormatted = new AbacusCurrency(fee, 'USD', true).toFormattedString();
    return (_jsx(_Fragment, { children: _jsxs("label", Object.assign({ htmlFor: 'card_shipping_require_signature' }, { children: [_jsx(Checkbox, { id: "card_shipping_require_signature", color: "secondary", onChange: () => {
                        onChange({
                            shippingRequireSignature: available ? !checked : false
                        });
                    }, checked: checked }, "require_signature"), _jsxs("span", { children: ["Require Signature (Extra charge of ", feeFormatted, ")"] })] })) }));
}
export const ShippingMethodForm = React.memo(ShippingMethodFormBase);
